import { html, nothing, type TemplateResult, unsafeCSS } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ref } from 'lit/directives/ref.js';
import App from '../../../../app';
import { UIModalServiceModal } from '../../../../app/package/base/service/modals/modal-service';
import { I18n } from '../../../base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../../base/package/util/a11y';
import { getPathNavigateToIncentiveInternalPage } from '../../../mod-incentive/helper/routes';
import { INCENTIVE_TAB_GROUPS, type IncentivePageView } from '../../../mod-incentive/types';
import { ArrowDirection } from '../../ui-arrow-button/ui-arrow-button';
import styles from './incentive-modal-container.css?inline';
import '../../ui-icons/ui-chevron-arrow-icon';

const CName = 'ui-incentive-modal-container';
const ROOT_VIEWS: IncentivePageView[] = ['base', 'reward-claim', 'mini-game'];

interface NavigationState {
  view: IncentivePageView;
  id?: string;
}
interface BottomBarOptions {
  detail: TemplateResult;
}

@customElement(CName)
export class IncentiveModalContainer extends UIModalServiceModal {
  static readonly styles = unsafeCSS(styles);

  public initialView?: { view: IncentivePageView; id?: string };
  /** When true, exiting the reward-claim view closes the modal and navigates to the page route. */
  public navigateToPageOnExit = false;

  @state() _headerTitle = 'Incentive EN';
  @state() _currentState: NavigationState = { view: 'base' };
  @state() _history: NavigationState[] = [];
  @state() _modalBottomBar?: BottomBarOptions;
  @query('#sentinel') private readonly _sentinel?: HTMLDivElement;

  private _opacity = '0';
  private readonly _abortController = new AbortController();

  public close() {
    this._modalRef.value?.close();
  }

  connectedCallback() {
    super.connectedCallback();
    this._resetState();

    if (this.initialView) {
      this._currentState = this.initialView;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._abortController.abort();
    // Both globals this container publishes. They live on different elements
    // on purpose: the incentive theme tokens that consume --incentive-bg-img
    // are declared on `body`, and a custom property referenced from another
    // custom property resolves against the element it was declared on. Moving
    // this to documentElement would leave those tokens unresolved.
    document.documentElement.style.removeProperty('--incentive-bottom-bar-height');
    document.body.style.removeProperty('--incentive-bg-img');
  }

  protected firstUpdated() {
    const container = this._modalRef.value;
    if (!container || !this._sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const progress = 1 - entry!.intersectionRatio;
        const normalized = Math.min(progress * 1.5, 1);
        const newOpacity = normalized.toFixed(1);

        if (newOpacity !== this._opacity) {
          this._opacity = newOpacity;
          this.style.setProperty('--header-opacity', this._opacity);
        }
      },
      {
        root: null,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );

    observer.observe(this._sentinel);

    this._abortController.signal.addEventListener('abort', () => {
      observer.disconnect();
    });
  }

  render() {
    const { view, id } = this._currentState;
    const mainClasses = classMap({
      'incentive-modal': true,
      'incentive-modal--full-bleed': view === 'reward-claim',
    });

    return html`
      <ui-modal
        ${ref(this._modalRef)}
        id="incentive-modal"
        class="modal--wide modal--incentive"
        .hideCloseButton=${true}
        .useBodyScrollLock=${false}
      >
        <div slot="main" class=${mainClasses}>
          <div aria-hidden="true" id="sentinel"></div>
          ${this._renderHeader(view === 'reward-claim')}
          <incentive-view
            class="incentive-view"
            .isModalView=${true}
            .view=${view}
            .id=${id}
            @ui-games-carousel-click=${() => this.close()}
            @ui-incentive-game-tile-deck-click=${() => this.close()}
            @incentive-modal-close=${() => this.close()}
            @incentive-modal-navigate-event=${this._onNavigate}
            @header-options-update=${this._updateHeader}
            @updateMobileBottomBar=${this._updateFooter}
          ></incentive-view>
        </div>
        <div slot="footer" class="modal-footer--incentive">
          ${this._renderMobileBottomBar()}
        </div>
      </ui-modal>
    `;
  }

  private _renderHeader(isHeaderHidden: boolean) {
    if (isHeaderHidden) {
      return nothing;
    }

    return html`
      <div class="header">
        <div class="header__right">
          ${
            this._history.length > 0
              ? html`
            <div class="incentive-modal__back-button"
              @click=${this._navigateBack}
              @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, () => this._navigateBack(ev))}
              tabindex="0"
              role="button"
              aria-label=${window.$app.$t.get(I18n.base.back)}
            >
              <ui-chevron-arrow-icon
                .arrowDirection=${ArrowDirection.LEFT}
              ></ui-chevron-arrow-icon>
              <div class="header__title">${this._headerTitle}</div>
            </div>
            `
              : html`<div class="header__title">${this._headerTitle}</div>`
          }
        </div>
        <ui-close-icon class="header__close" @click=${() => this.close()}
          @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this.close.bind(this))}
          tabindex="0"
          aria-label=${window.$app.$t.get(I18n.a11y.button.close)}
        ></ui-close-icon>
      </div>
    `;
  }

  private _renderMobileBottomBar() {
    if (this._modalBottomBar?.detail) {
      return html`${this._modalBottomBar?.detail}`;
    }

    return nothing;
  }

  private _updateHeader(e: CustomEvent) {
    e.stopPropagation();
    this._headerTitle = e.detail.title;
  }

  private _updateFooter(e: CustomEvent) {
    e.stopPropagation();
    this._modalBottomBar = e.detail;
    this._publishIncentiveBarHeight();
  }

  private _publishIncentiveBarHeight(): void {
    requestAnimationFrame(() => {
      const bar = this.shadowRoot?.querySelector('.bottom-bar.incentive');
      if (bar) {
        const height = bar.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--incentive-bottom-bar-height', `${height}px`);
      }
    });
  }

  private _onNavigate(e: CustomEvent) {
    e.stopPropagation();
    const detail = e.detail;

    const nextView: IncentivePageView = detail.view ? detail.view : detail;
    const nextId: string | undefined = detail.id;

    // When opened from lobby context, exit reward-claim by closing the modal
    // and navigating to the appropriate gamification page route.
    if (this.navigateToPageOnExit && this._currentState.view === 'reward-claim' && nextView !== 'reward-claim') {
      this.close();
      const url = getPathNavigateToIncentiveInternalPage(nextView, nextId);
      App.instance.router.navigateTo(url);
      return;
    }

    if (ROOT_VIEWS.includes(nextView)) {
      this._history = [];
    } else {
      const isTabSwitch = INCENTIVE_TAB_GROUPS.some(
        (group) => group.includes(this._currentState.view) && group.includes(nextView),
      );

      if (!isTabSwitch) {
        this._history.push(this._currentState);
      }
    }

    this._currentState = { view: nextView, id: nextId };
    this._resetState();
  }

  private _navigateBack(e: Event) {
    e.stopPropagation();
    const previousState = this._history.pop();

    if (previousState) {
      this._currentState = previousState;
    }

    this._resetState();
  }

  private _resetState() {
    this._modalBottomBar = undefined;
    document.body.style.setProperty('--incentive-bg-img', 'none');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: IncentiveModalContainer;
  }
}
